/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { OfferType } from "../../model/enums";
import type { TCarouselItem } from "../../model/types";
import {
  IdType,
  NdvrPlaybackType,
  PlaybackContentType
} from "../../model/videoplayer/enums";
import type { IPlayable } from "../../model/videoplayer/interfaces";
import type { ContentDetailSeed } from "../contentDetail/contentDetail.types";

const UNIFIED_COLLECTION_ID_PREFIX: string = "tivo:cl.";
const CONTENT_ID_PREFIX: string = "tivo:ct.";
const OFFER_ID_PREFIX: string = "tivo:of.";
const RECORDING_ID_PREFIX: string = "tivo:crc.";
const VOD_OFFER_ID_PREFIX: string = "tivo:mx.";

/**
 * Maps a carousel tile to the Content Detail navigation seed expected by
 * ContentDetailScreen, derived from the `tivo:*` namespace of the tile's
 * feed item id.
 *
 * Shared across every screen that opens Content Detail from a carousel tile
 * (Search, Home, …) — the seed shape is owned by
 * {@link ContentDetailSeed}, not by any one screen.
 *
 * Returns `null` when the feed item id belongs to no recognized namespace.
 * There is deliberately NO fallback seed: an unrecognized id cannot be
 * loaded by `loadContentDetail`, so guessing a seed type would only open
 * Content Detail on a guaranteed error state. Callers must treat `null` as
 * "this tile has no Content Detail destination" and stay where they are.
 *
 * @param item - The selected carousel tile
 * @returns The Content Detail seed, or `null` when the id is unrecognized
 */
export const mapContentDetailSeed = (
  item: TCarouselItem
): ContentDetailSeed | null => {
  const feedItemId: string = item.id;

  if (feedItemId.indexOf(UNIFIED_COLLECTION_ID_PREFIX) === 0) {
    return { seedType: "unifiedCollectionId", seedId: feedItemId };
  }

  if (feedItemId.indexOf(CONTENT_ID_PREFIX) === 0) {
    return { seedType: "contentId", seedId: feedItemId };
  }

  if (feedItemId.indexOf(OFFER_ID_PREFIX) === 0) {
    if (item.offerType === OfferType.Vod) {
      return { seedType: "vodOfferId", seedId: feedItemId };
    }
    if (item.offerType === OfferType.Recording) {
      return { seedType: "recordingId", seedId: feedItemId };
    }
    return { seedType: "offerId", seedId: feedItemId };
  }

  if (feedItemId.indexOf(RECORDING_ID_PREFIX) === 0) {
    return { seedType: "recordingId", seedId: feedItemId };
  }

  if (feedItemId.indexOf(VOD_OFFER_ID_PREFIX) === 0) {
    return { seedType: "vodOfferId", seedId: feedItemId };
  }

  return null;
};

/**
 * nDVR seed, keyed by the playback model's `ndvrType`.
 */
const mapNdvrSeed = (playable: IPlayable): ContentDetailSeed | null => {
  const ndvrModel = playable.ndvrPlaybackModel;
  if (ndvrModel === undefined || ndvrModel.id === "") {
    return null;
  }
  if (ndvrModel.ndvrType === NdvrPlaybackType.CONTENT) {
    return { seedType: "contentId", seedId: ndvrModel.id };
  }
  if (ndvrModel.ndvrType === NdvrPlaybackType.UNIFIED_COLLECTION) {
    return { seedType: "unifiedCollectionId", seedId: ndvrModel.id };
  }
  return { seedType: "recordingId", seedId: ndvrModel.id };
};

/**
 * VOD seed, keyed by the playback model's `idType`.
 */
const mapVodSeed = (playable: IPlayable): ContentDetailSeed | null => {
  const vodModel = playable.vodPlaybackModel;
  if (vodModel === undefined || vodModel.id === "") {
    return null;
  }
  if (vodModel.idType === IdType.OFFER) {
    return { seedType: "vodOfferId", seedId: vodModel.id };
  }
  if (vodModel.idType === IdType.UNIFIED_COLLECTION) {
    return { seedType: "unifiedCollectionId", seedId: vodModel.id };
  }
  return { seedType: "contentId", seedId: vodModel.id };
};

/**
 * Live, Start Over and Catch Up seed — all play an airing of a linear offer.
 */
const mapLinearSeed = (
  playable: IPlayable,
  currentOfferId?: string | null,
  currentContentId?: string | null
): ContentDetailSeed | null => {
  const offerId: string = playable.offerId || currentOfferId || "";
  if (offerId !== "") {
    return { seedType: "offerId", seedId: offerId };
  }
  const contentId: string = playable.contentId || currentContentId || "";
  if (contentId !== "") {
    return { seedType: "contentId", seedId: contentId };
  }
  return null;
};

/**
 * Maps the playable currently in the player to the Content Detail navigation
 * seed, so the trickplay "Details" action opens the programme being watched.
 *
 * The seed type follows the playback type:
 *  - nDVR (in-progress or completed recording) → by the playback model's
 *    `ndvrType`: `recordingId`, `contentId` or `unifiedCollectionId`.
 *  - VOD → by the playback model's `idType`: `vodOfferId` for an offer id,
 *    `unifiedCollectionId` for a collection id, `contentId` otherwise (the
 *    default VOD id kind).
 *  - Live, Start Over and Catch Up → `offerId` of the airing, falling back to
 *    a `contentId` seed when no offer id is known.
 *
 * Like {@link mapContentDetailSeed} there is no guessed fallback: `null`
 * means the playable carries no id Content Detail can load.
 *
 * @param playable - The playable currently in the player, or null when none
 * @param currentOfferId - Offer id resolved from the playback session's
 *   metadata, used when the playable itself carries none (e.g. Live)
 * @param currentContentId - Content id resolved from the playback session's
 *   metadata, used when the playable itself carries none
 * @returns The Content Detail seed, or `null` when no id is available
 */
export const mapPlayableContentDetailSeed = (
  playable: IPlayable | null,
  currentOfferId?: string | null,
  currentContentId?: string | null
): ContentDetailSeed | null => {
  if (playable === null) {
    return null;
  }

  switch (playable.playbackContentType) {
    case PlaybackContentType.NDVR_LIVE:
    case PlaybackContentType.NDVR_RECORDED:
      return mapNdvrSeed(playable);
    case PlaybackContentType.VOD:
      return mapVodSeed(playable);
    default:
      return mapLinearSeed(playable, currentOfferId, currentContentId);
  }
};
